import type { PackagingInformationEntry } from "~/domains/product/types/ProductData";
import { Card } from "~/features/configurator/Linear/components/Card";
import { type ReactNode, useMemo, useState } from "react";
import { ToggleIcon } from "~/common/components/icon/ToggleIcon";
import { CheckedCircle } from "~/common/components/CheckedCircle/CheckedCircle";
import { Collapse, Divider, IconButton, Stack, Typography, type TypographyProps, styled, Box } from "~/common/mui";
import { getStockLevel, StockIndicatorIcon } from "~/domains/product/components/StockIndicator/StockIndicator";
import { Icon } from "~/common/components/icon/Icon";
import { IconType } from "~/common/components/icon/IconType";
import {
  AliasesParameter,
  CountryOfOriginDescriptorParam,
  EANParameter,
} from "~/domains/product/components/ProductParams";
import { useTranslation } from "react-i18next";
import { getPackagingLabel } from "~/features/cart/cart/utils/cartUtils";
import { Table, TableBody, TableCell, TableHead, TableRow } from "~/common/components/Table/TableElements";
import dayjs from "dayjs";
import { DateFormatType, formatDate } from "~/common/utils/DateFormatter";
import { useIsPackagePhasedOut } from "~/features/productDetails/components/PhaseInOut/usePhaseInOutLogic";
import { DiscontinuationNotification } from "~/features/productDetails/components/PhaseInOut/DiscontinuationNotification/DiscontinuationNotification";
import { PackagingSelectionVariant } from "~/domains/product/components/PackagingSelection/PackagingSelectionVariant";

import Parameter from "~/common/components/Parameter";
import { calculateSalesCampaign4CampaignPage } from "~/features/salesCampaign/utils/salesCampaignUtils";
import CircleSpinner from "~/common/components/CircleSpinner/CircleSpinner";
import { SaleQuantityIndicator } from "~/domains/salesCampaign/components/SaleQuantityIndicator/SaleQuantityIndicator";
import { SaleTag } from "~/domains/salesCampaign/components/SaleTag/SaleTag";
import { useSalesData } from "~/features/salesCampaign/hooks/useSalesData";
import { useNumericScreenSize } from "~/common/hooks/useNumericScreenSize";
import { NumberFormatter } from "~/common/utils/NumberFormatter";
import { translateWarehouseName } from "~/utils/language/translationUtils";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import { showDateAsCalendarWeek } from "~/utils/features";

interface Props {
  className?: string;
  pack: PackagingInformationEntry;
  productDescriptor: ProductDescriptor;
  selected?: boolean;
  onSelect?: () => void;
  initiallyExpanded?: boolean;
  variant: PackagingSelectionVariant;
  disabled?: boolean;
}

const RESULT_NF = NumberFormatter.getFormatter();
const CARD_PADDING = 16;

// Styled components
const Root = styled(Card)(({ theme }) => ({
  "&&": {
    background: theme.palette.common.white,
    padding: 0,
    display: "flex",
    flexFlow: "column",
    position: "relative",
  },
}));

const CardContentStyled = styled(Box)(({ theme }) => ({
  padding: CARD_PADDING,
  display: "flex",
  flexFlow: "column",
  gap: theme.spacing(2),
  "&:empty": {
    display: "none",
  },
}));

const PerPackStack = styled(Stack)({
  alignItems: "center",
});

const PreferredPackaging = styled("p")(({ theme }) => ({
  marginTop: theme.spacing(-1),
}));

const Title = styled("p")(({ theme }) => ({
  ...theme.typography.titleMedium,
  margin: 0,
}));

const SectionTitle = styled("p")({
  fontWeight: "bold",
  margin: 0,
  alignItems: "center",
});

const CollapsedTitleText = styled("p")(({ theme }) => ({
  color: theme.palette.text.label,
}));

const HeaderStyled = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(1),
  alignItems: "center",
}));

const ToggleButton = styled(IconButton)({
  "&&": {
    margin: "-12px -12px -12px auto",
  },
});

const CheckedIndicatorButton = styled(IconButton)({
  "&&": {
    margin: "-8px",
  },
});

const CollapsibleCardStyled = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2),
  display: "flex",
  flexFlow: "column",
}));

const CollapsibleCardContent = styled(Box)(({ theme }) => ({
  paddingTop: theme.spacing(2),
  "& > table": {
    "& th": {
      color: theme.palette.text.label,
    },
    "& th, & td": {
      padding: `${theme.spacing(0.5)} ${theme.spacing(2)}`,
      maxWidth: "min-content",
    },
  },
}));

const MiscSectionStyled = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(1),
  flexDirection: "column",
  [theme.breakpoints.up("tablet")]: {
    flexDirection: "row",
    gap: theme.spacing(2),
  },
  "& > *:nth-of-type(1)": {
    flex: 4,
  },
  "& > *:nth-of-type(2)": {
    flex: 3,
  },
  "& > *:nth-of-type(3)": {
    flex: 3,
  },
}));

const StockTableStyled = styled(Table)(({ theme }) => ({
  tableLayout: "fixed",
  "& th:first-of-type": {
    width: 96,
  },
  "& th, & td": {
    padding: `${theme.spacing(1)} ${theme.spacing(2)}`,
  },
}));

const SeparateHeaderContainer = styled(Box)(({ theme }) => ({
  position: "relative",
  borderBottom: `1px solid ${theme.palette.divider}`,
  "&:has(+ *:empty)": {
    borderBottom: "none",
  },
}));

const SeparateHeader = styled(HeaderStyled)({
  gap: 64,
  "& > *:first-child": {
    width: 190,
    flex: "none",
  },
});

const SeparateHeaderParams = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
  width: "100%",
  gap: theme.spacing(2),
  [theme.breakpoints.up("desktop-m")]: {
    gap: theme.spacing(4),
  },
  [theme.breakpoints.up("desktop-l")]: {
    gap: theme.spacing(8),
  },
}));

const DividerStyled = styled(Divider)({
  "&&": {
    marginBlock: -CARD_PADDING,
    marginInline: -32,
  },
});

const SalesTableTagSpan = styled("span")(({ theme }) => ({
  display: "inline-flex",
  marginLeft: theme.spacing(4),
}));

const SaleTagAbsolute = styled(SaleTag)({
  position: "absolute",
  top: 0,
  left: 0,
  zIndex: 2,
});

export const NewPackagingSelectionItem = ({
  selected,
  initiallyExpanded,
  onSelect,
  productDescriptor,
  pack,
  variant,
  disabled,
}: Props) => {
  const { t } = useTranslation();

  const screenSize = useNumericScreenSize();
  const isMobile = screenSize <= useNumericScreenSize.tablet;

  const [{ stockExpanded, productionExpanded, miscExpanded }, setState] = useState(() => ({
    stockExpanded: initiallyExpanded,
    productionExpanded: initiallyExpanded,
    miscExpanded: initiallyExpanded,
  }));
  const allExpanded = stockExpanded && productionExpanded && miscExpanded;
  const toggleAll = () => {
    setState({
      stockExpanded: !allExpanded,
      productionExpanded: !allExpanded,
      miscExpanded: !allExpanded,
    });
  };
  const makeToggle = (key: "stockExpanded" | "productionExpanded" | "miscExpanded") => () => {
    setState((state) => ({ ...state, [key]: !state[key] }));
  };

  const saleForPack = useSalesData()?.salePerPack?.[pack.packCode];

  const packagingLabel = getPackagingLabel({
    t,
    productDescriptor,
    packSize: pack.packSize,
    packCode: pack.packCode,
  });

  const prefQuantity = pack.preferredPackQuantity;

  const showDiscontinuedWarning = useIsPackagePhasedOut(productDescriptor, pack.packCode);

  const showSectionToggles =
    variant === PackagingSelectionVariant.PDP || (variant === PackagingSelectionVariant.SRP && isMobile);
  const showPreferredQuantity =
    variant === PackagingSelectionVariant.PDP || (variant === PackagingSelectionVariant.SRP && isMobile);
  const showMiscSection =
    variant === PackagingSelectionVariant.PDP || (variant === PackagingSelectionVariant.SRP && isMobile);

  const separateHeader =
    (!isMobile && variant === PackagingSelectionVariant.SRP) || variant === PackagingSelectionVariant.Campaign;

  const showMiscParamsInHeader = !isMobile && variant === PackagingSelectionVariant.SRP;
  const showParamsInHeader = showMiscParamsInHeader;

  const stockCalculations = useInStockCalculations(pack);

  const showExpandAll = variant !== PackagingSelectionVariant.Campaign;
  const showMainContent = variant !== PackagingSelectionVariant.Campaign;

  const mainContent = (
    <>
      {showPreferredQuantity && !!prefQuantity && (
        <PreferredPackaging> {t("addToCartPopup.prefPackQuantity", { count: prefQuantity })}</PreferredPackaging>
      )}
      <InStockSection
        expanded={separateHeader || stockExpanded}
        onToggle={makeToggle("stockExpanded")}
        pack={pack}
        showToggle={showSectionToggles}
        {...stockCalculations}
      />
      <InProductionSection
        expanded={separateHeader || productionExpanded}
        onToggle={makeToggle("productionExpanded")}
        pack={pack}
        showToggle={showSectionToggles}
      />
      {showMiscSection && (
        <MiscSection
          expanded={separateHeader || miscExpanded}
          onToggle={makeToggle("miscExpanded")}
          pack={pack}
          productDescriptor={productDescriptor}
          showToggle={showSectionToggles}
        />
      )}
    </>
  );

  const discontinuationNotification = showDiscontinuedWarning && (
    <DiscontinuationNotification discontinued={productDescriptor} variant={"package"} />
  );

  const header = (
    <Header expanded={allExpanded} onToggle={toggleAll} showToggle={showExpandAll} separate={separateHeader}>
      <PerPackStack direction={"row"} gap={1}>
        <CheckedIndicatorButton onClick={onSelect} disabled={disabled}>
          <CheckedCircle checked={selected} alwaysVisible />
        </CheckedIndicatorButton>
        <Title>{packagingLabel}</Title>
      </PerPackStack>
      {saleForPack && <SaleTagAbsolute size={20} variant={saleForPack.soldOut ? "inactive" : "active"} />}
      {showParamsInHeader && (
        <>
          <DividerStyled variant={"fullWidth"} orientation={"vertical"} flexItem />
          {showMiscParamsInHeader && (
            <SeparateHeaderParams>
              {stockCalculations.totalInStock > 0 && (
                <Parameter label={"Availability"}>
                  <StockIndicatorValue totalInStock={stockCalculations.totalInStock} />
                </Parameter>
              )}
              {stockCalculations.totalInStock === 0 && (
                <Parameter label={<StockIndicatorValue totalInStock={0} />}>
                  <ReplenishmentDate date={pack.replenishmentLeadTimeDate} component={"span"} />
                </Parameter>
              )}
              <EANParameter showEmpty product={productDescriptor.base} packCode={pack.packCode} />
              <CountryOfOriginDescriptorParam showEmpty descriptor={productDescriptor} packCode={pack.packCode} />
              <AliasesParameter
                showEmpty
                product={productDescriptor.base}
                mode={"15-digits"}
                maxAliasesVisible={1}
                packCode={pack.packCode}
              />
            </SeparateHeaderParams>
          )}
        </>
      )}
    </Header>
  );

  let content = (
    <CardContentStyled>
      {discontinuationNotification}
      {!separateHeader && header}
      {showMainContent && mainContent}
    </CardContentStyled>
  );

  if (separateHeader) {
    content = (
      <Collapse in={allExpanded} classes={{ wrapperInner: "collapse-content" }}>
        <CollapsibleCardContent>{content}</CollapsibleCardContent>
      </Collapse>
    );
  }

  return (
    <Root active={selected}>
      {separateHeader && (
        <SeparateHeaderContainer>
          <CardContentStyled>{header}</CardContentStyled>
        </SeparateHeaderContainer>
      )}
      {content}
    </Root>
  );
};

interface CollapsibleSectionProps {
  expanded?: boolean;
  onToggle: () => void;
  showToggle?: boolean;
  canExpand?: boolean;
  title: ReactNode;
  children: ReactNode;
}

const CollapsibleSection = ({
  expanded,
  onToggle,
  showToggle,
  canExpand,
  children,
  title,
}: CollapsibleSectionProps) => {
  return (
    <CollapsibleCardStyled>
      <Header expanded={expanded} onToggle={onToggle} showToggle={showToggle}>
        {title}
      </Header>
      {canExpand && (
        <Collapse in={expanded}>
          <CollapsibleCardContent>{children}</CollapsibleCardContent>
        </Collapse>
      )}
    </CollapsibleCardStyled>
  );
};

interface HeaderProps {
  children: ReactNode;
  expanded?: boolean;
  onToggle: () => void;
  showToggle?: boolean;
  separate?: boolean;
}

const Header = ({ children, expanded, onToggle, showToggle, separate }: HeaderProps) => {
  const HeaderComponent = separate ? SeparateHeader : HeaderStyled;
  return (
    <HeaderComponent>
      {children}
      {showToggle && (
        <ToggleButton onClick={onToggle} color={"primary"}>
          <ToggleIcon isActive={expanded} />
        </ToggleButton>
      )}
    </HeaderComponent>
  );
};

const CollapsedTitle = ({ children }: { children: ReactNode }) => {
  return (
    <>
      <Divider orientation={"vertical"} flexItem />
      <CollapsedTitleText>{children}</CollapsedTitleText>
    </>
  );
};

type StockTableProps = {
  warehouseName: string;
  stock: (readonly [number, string])[];
  salesCampaignStock?: number;
};

const StockTable = ({ warehouseName, stock, salesCampaignStock = 0 }: StockTableProps) => {
  const { t } = useTranslation();
  return (
    <StockTableStyled>
      <TableHead>
        <TableRow>
          <TableCell>{t("productDetailsPage.availabilityTable.units")}</TableCell>
          <TableCell>
            {window.SCHA.config.showESD
              ? warehouseName
              : t("productDetailsPage.availabilityTable.estimatedArrivalDate")}
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {salesCampaignStock > 0 && (
          <TableRow>
            <TableCell>{NumberFormatter.getDefaultFormatter().format(salesCampaignStock)}</TableCell>
            <TableCell>
              <span>{t("productDetailsPage.availabilityTable.readyForShipment", { quantity: "" })}</span>
              <SalesTableTagSpan>
                {t("feature.salesCampaign.specialDealPrice")}
                <SaleTag className={"scha__pkg-selection__table__sales-tag"} size={20} />
              </SalesTableTagSpan>
            </TableCell>
          </TableRow>
        )}
        {stock.map(([amount, date], index) => (
          <TableRow key={index}>
            <TableCell>{NumberFormatter.getDefaultFormatter().format(amount)}</TableCell>
            <TableCell>{date}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </StockTableStyled>
  );
};

interface SectionProps {
  pack: PackagingInformationEntry;
  expanded?: boolean;
  onToggle: () => void;
  showToggle?: boolean;
}

const InStockSection = ({
  pack,
  expanded,
  onToggle,
  showToggle = true,
  readyForShipment,
  totalInStock,
  inStockEntries,
}: SectionProps & ReturnType<typeof useInStockCalculations>) => {
  const { t } = useTranslation();

  const warehouseName = translateWarehouseName(t, pack.primaryWarehouseName);
  const salesStock = useSalesData()?.salePerPack?.[pack.packCode]?.salesStock;
  const firstStockEntry = inStockEntries[0];

  const canExpand = totalInStock > 0;
  const quantity = firstStockEntry ? Number(firstStockEntry[0]) : 0;

  return (
    <CollapsibleSection
      expanded={expanded && canExpand}
      onToggle={onToggle}
      showToggle={showToggle && canExpand}
      canExpand={canExpand}
      title={
        <>
          <StockIndicatorValue as={SectionTitle} totalInStock={totalInStock} />
          {!expanded && firstStockEntry && canExpand && (
            <CollapsedTitle>
              {t("productDetailsPage.availabilityTable.upcoming", {
                quantity: isNaN(quantity) ? firstStockEntry[0] : RESULT_NF.format(quantity),
                date: firstStockEntry[1],
              })}
            </CollapsedTitle>
          )}
        </>
      }
    >
      <StockTable warehouseName={warehouseName} stock={inStockEntries} salesCampaignStock={salesStock} />
    </CollapsibleSection>
  );
};

const useInStockCalculations = (pack: PackagingInformationEntry) => {
  const { t } = useTranslation();
  return useMemo(() => {
    let inStockEntries;
    let readyForShipment;
    if (showDateAsCalendarWeek()) {
      inStockEntries = pack.totalWarehouse.inStockWeekly.map((entry) => {
        const formattedDate = entry.cwDate
          ? entry.cwDate
          : t("productDetailsPage.availabilityTable.readyForShipment", { quantity: "" });
        return [entry.quantity, formattedDate] as const;
      });
      readyForShipment = pack.totalWarehouse.inStockWeekly.find((entry) => !entry.cwDate)?.quantity ?? 0;
    } else {
      inStockEntries = pack.totalWarehouse.inStock.map((entry) => {
        const formattedDate = entry.date
          ? formatDate(entry.date, DateFormatType.DATE)
          : t("productDetailsPage.availabilityTable.readyForShipment", { quantity: "" });
        return [entry.quantity, formattedDate] as const;
      });
      readyForShipment = pack.totalWarehouse.inStock.find((entry) => !entry.date)?.quantity ?? 0;
    }

    const totalInStock = inStockEntries.reduce((acc, [quantity]) => acc + quantity, 0);
    return { readyForShipment, inStockEntries, totalInStock };
  }, [pack, t]);
};

const StockIndicatorValue = ({ totalInStock, as }: { totalInStock: number; as?: React.ElementType }) => {
  const { t } = useTranslation();
  const Component = as ?? "p";

  return (
    <Stack direction={"row"} gap={1} sx={{ display: "flex", alignItems: "center" }}>
      <StockIndicatorIcon level={getStockLevel(totalInStock)} />
      <Component>
        {totalInStock > 0 &&
          t("stockIndicator.existsWithQty", { amount: NumberFormatter.getDefaultFormatter().format(totalInStock) })}
        {totalInStock === 0 && t("stockIndicator.currentlyNotInStock")}
      </Component>
    </Stack>
  );
};

const InProductionSection = ({ pack, expanded, onToggle, showToggle = true }: SectionProps) => {
  const { t } = useTranslation();

  let inProductionEntries;
  if (showDateAsCalendarWeek()) {
    inProductionEntries = pack.totalWarehouse.inProductionWeekly.map((entry) => {
      const formattedDate = entry.cwDate;
      return [entry.quantity, formattedDate] as const;
    });
  } else {
    inProductionEntries = pack.totalWarehouse.inProduction.map((entry) => {
      const formattedDate = dayjs(entry.date).format("DD.MM.YYYY");
      return [entry.quantity, formattedDate] as const;
    });
  }

  const firstUpcomingStock = inProductionEntries[0];
  const stockValue = firstUpcomingStock ? Number(firstUpcomingStock[0]) : 0;

  if (pack.totalWarehouse.inClarification > 0) {
    inProductionEntries.unshift([
      pack.totalWarehouse.inClarification,
      t("product.availability.stock.table.inClarification"),
    ] as const);
  }
  const totalInProduction = inProductionEntries.reduce((acc, [quantity]) => acc + quantity, 0);

  const warehouseName = translateWarehouseName(t, pack.primaryWarehouseName);

  const canExpand = totalInProduction > 0;
  return (
    <CollapsibleSection
      expanded={expanded}
      onToggle={onToggle}
      showToggle={showToggle && canExpand}
      canExpand={canExpand}
      title={
        <>
          <PerPackStack direction={"row"} gap={1}>
            <Icon type={IconType.Calendar} />
            {totalInProduction > 0 && (
              <SectionTitle>
                {t("productDetailsPage.availabilityTable.toCome", {
                  quantity: RESULT_NF.format(totalInProduction),
                })}
              </SectionTitle>
            )}
            {totalInProduction === 0 && <ReplenishmentDate date={pack.replenishmentLeadTimeDate} />}
          </PerPackStack>
          {!expanded && firstUpcomingStock && (
            <CollapsedTitle>
              {t("productDetailsPage.availabilityTable.upcoming", {
                quantity: isNaN(stockValue) ? firstUpcomingStock[0] : RESULT_NF.format(stockValue),
                date: firstUpcomingStock[1],
              })}
            </CollapsedTitle>
          )}
        </>
      }
    >
      <StockTable warehouseName={warehouseName} stock={inProductionEntries} />
      <ReplenishmentDate date={pack.replenishmentLeadTimeDate} sx={{ mt: 2 }} />
    </CollapsibleSection>
  );
};

const MiscSection = ({
  pack,
  expanded,
  onToggle,
  productDescriptor,
  showToggle = true,
}: SectionProps & { productDescriptor: ProductDescriptor }) => {
  const { t } = useTranslation();
  return (
    <CollapsibleSection
      expanded={expanded}
      onToggle={onToggle}
      showToggle={showToggle}
      canExpand={true}
      title={
        <>
          <PerPackStack direction={"row"} gap={1}>
            <Icon type={IconType.InfoCircleOutline} />
            <SectionTitle>{t("page.plp.tab.moreInfo")}</SectionTitle>
          </PerPackStack>
          {!expanded && <CollapsedTitle>{t("productDetailsPage.availabilityTable.miscParams")}</CollapsedTitle>}
        </>
      }
    >
      <MiscSectionStyled>
        <div>
          <AliasesParameter
            showEmpty
            product={productDescriptor.base}
            mode={"15-digits"}
            maxAliasesVisible={2}
            packCode={pack.packCode}
          />
        </div>
        <div>
          <EANParameter showEmpty product={productDescriptor.base} packCode={pack.packCode} />
        </div>
        <div>
          <CountryOfOriginDescriptorParam showEmpty descriptor={productDescriptor} packCode={pack.packCode} />
        </div>
      </MiscSectionStyled>
    </CollapsibleSection>
  );
};

const SalesCampaignStock = ({ pack, productDescriptor }: Pick<Props, "pack" | "productDescriptor">) => {
  const firstSalesCampaignObj = Object.values(productDescriptor.base.salesCampaign ?? {})?.[0]?.[0];
  const salesCampaignCode = firstSalesCampaignObj?.code;

  const { status: salesStatus, data } = useMemo(
    () => calculateSalesCampaign4CampaignPage({ productDescriptor, salesCampaignCode }),
    [productDescriptor, salesCampaignCode],
  );

  if (salesStatus === "loading") {
    return <CircleSpinner />;
  }

  const salesData = data?.salePerPack?.[pack.packCode];

  if (!salesData) {
    return null;
  }

  return <SaleQuantityIndicator quantity={salesData.salesStock} />;
};

type ReplenishmentDateProps = Omit<TypographyProps, "children"> & {
  date: Date | null;
};

const ReplenishmentDate = ({ date, ...props }: ReplenishmentDateProps) => {
  const { t } = useTranslation();

  let dateString;
  if (date) {
    if (showDateAsCalendarWeek()) {
      const months = Math.ceil(dayjs(date).diff(dayjs(), "month", true));
      dateString = t("common.month", { count: months });
    } else {
      dateString = formatDate(date, DateFormatType.DATE);
    }
  } else {
    dateString = "-";
  }

  return (
    <Typography fontWeight={"normal"} {...props}>
      {t("addProduct.replenishment", { date: "" })}
      <b>{dateString}</b>
    </Typography>
  );
};
